import React, { useState, useRef } from 'react';
import {
  Button,
  Card,
  CardContent,
  LinearProgress,
  Typography,
  Box,
  useTheme,
  Alert,
  Stack,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { AssistantService } from '../../api/AssistantService';
import { useQueryClient } from 'react-query';
import { useFetchPendingDocuments } from '../../hooks/useFetchObject';
import { shouldSplitPDF, splitPDF, chunksToFiles } from './SplitPDF';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useUploadStore } from '../../stores/uploadStore';

// Size threshold for splitting PDFs (5 MB)
const PDF_SIZE_THRESHOLD = 5 * 1024 * 1024;

export const UploadDocument = () => {
  const theme = useTheme();
  const selectedProject = useStore(useUnityBuildStore, state => state.selectedProject);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { refetch: refetchPendingDocuments } = useFetchPendingDocuments(selectedProject.id);

  // Use our upload store
  const { startUpload, activeUploads } = useUploadStore();

  const [files, setFiles] = useState<File[]>([]);
  const [originalFiles, setOriginalFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [fileTypeError, setFileTypeError] = useState<string | null>(null);
  const [processingMessage, setProcessingMessage] = useState<string | null>(null);
  const [splitInfo, setSplitInfo] = useState<
    Record<string, { isSplit: boolean; chunks: number; totalPages: number }>
  >({});
  const [showSplitDetails, setShowSplitDetails] = useState<Record<string, boolean>>({});
  const [isSplittingFiles, setIsSplittingFiles] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  // Get the current upload job if it exists
  const currentJob = currentJobId ? activeUploads.find(job => job.id === currentJobId) : null;

  // Extract progress and errors from the current job
  const progress = currentJob
    ? Object.entries(currentJob.status).reduce(
        (acc, [filename, status]) => {
          acc[filename] = status.progress;
          return acc;
        },
        {} as Record<string, string>
      )
    : {};

  const errors = currentJob
    ? Object.entries(currentJob.status).reduce(
        (acc, [filename, status]) => {
          if (status.error) {
            acc[filename] = status.error;
          }
          return acc;
        },
        {} as Record<string, string>
      )
    : {};

  const handleUploadClick = () => {
    if (inputRef.current) {
      setFiles([]);
      setOriginalFiles([]);
      setIsUploading(false);
      setUploadComplete(false);
      setFileTypeError(null);
      setProcessingMessage(null);
      setSplitInfo({});
      setShowSplitDetails({});
      setCurrentJobId(null);
      inputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileTypeError(null);
    setProcessingMessage(null);

    if (event.target.files) {
      const selectedFiles = Array.from(event.target.files);

      // Filter for only PDF files
      const pdfFiles = selectedFiles.filter(
        file => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
      );

      // Check if any non-PDF files were selected
      if (pdfFiles.length < selectedFiles.length) {
        setFileTypeError('Only PDF files are allowed. Non-PDF files have been removed.');
      }

      // If no valid files remain
      if (pdfFiles.length === 0 && selectedFiles.length > 0) {
        setFileTypeError('Please select PDF files only.');
        setFiles([]);
        setOriginalFiles([]);
        return;
      }

      // Set original files first
      setOriginalFiles(pdfFiles);

      // Check if any files need splitting
      const hasLargeFiles = pdfFiles.some(file => shouldSplitPDF(file, PDF_SIZE_THRESHOLD));

      if (hasLargeFiles) {
        setIsSplittingFiles(true);
        setProcessingMessage('Analyzing large PDFs and preparing for upload...');

        // Process each file for splitting
        const processedFiles: File[] = [];
        const newSplitInfo: Record<
          string,
          { isSplit: boolean; chunks: number; totalPages: number }
        > = {};

        for (const file of pdfFiles) {
          if (shouldSplitPDF(file, PDF_SIZE_THRESHOLD)) {
            setProcessingMessage(`Splitting large PDF: ${file.name}`);
            try {
              // Split the PDF
              const result = await splitPDF(file);
              const chunkFiles = chunksToFiles(result, file.name);

              // Add split info
              newSplitInfo[file.name] = {
                isSplit: true,
                chunks: chunkFiles.length,
                totalPages: result.totalPages,
              };

              // Add a default entry for showing split details
              setShowSplitDetails(prev => ({
                ...prev,
                [file.name]: false,
              }));

              // Add the chunks to our processed files
              processedFiles.push(...chunkFiles);

              setProcessingMessage(
                `Successfully split "${file.name}" into ${chunkFiles.length} parts`
              );
            } catch (error) {
              console.error(`Error splitting PDF ${file.name}:`, error);

              // Add the original file if splitting fails
              processedFiles.push(file);
              newSplitInfo[file.name] = {
                isSplit: false,
                chunks: 1,
                totalPages: 0,
              };
            }
          } else {
            // File doesn't need splitting
            processedFiles.push(file);
            newSplitInfo[file.name] = {
              isSplit: false,
              chunks: 1,
              totalPages: 0,
            };
          }
        }

        // Update state with processed files
        setFiles(processedFiles);
        setSplitInfo(newSplitInfo);
        setIsSplittingFiles(false);
        setProcessingMessage('Files are ready to upload');
      } else {
        // No files need splitting
        setFiles(pdfFiles);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (files.length === 0) return;

    setIsUploading(true);
    setUploadComplete(false);
    setProcessingMessage('Starting upload process...');

    try {
      // Start the upload process using our store
      const jobId = await startUpload(files, selectedProject.id);
      setCurrentJobId(jobId);

      // Indicate that the upload has started and can continue in the background
      setProcessingMessage(
        'Upload started. You can navigate away and the upload will continue in the background.'
      );

      // After a delay, check if vector store should be updated
      setTimeout(async () => {
        try {
          await AssistantService.addGCSObjectsToVectorStore({
            projectid: selectedProject.id,
            objectid: '',
          });

          // Invalidate queries
          queryClient.invalidateQueries('pending-documents');
          refetchPendingDocuments();
        } catch (err) {
          console.error('Error adding GCS objects to vector store:', err);
        }
      }, 5000);
    } catch (err) {
      console.error('Error starting upload:', err);
      setProcessingMessage('There was an error starting the upload. Please try again.');
    } finally {
      // We consider this complete from the UI perspective once started
      // even though actual uploads continue in the background
      setTimeout(() => {
        setIsUploading(false);
        setUploadComplete(true);
      }, 1000);
    }
  };

  const toggleSplitDetails = (fileName: string) => {
    setShowSplitDetails(prev => ({
      ...prev,
      [fileName]: !prev[fileName],
    }));
  };

  // Group files by original file name for better display
  const getOriginalFileName = (fileName: string): string => {
    // Find the original filename this might be a part of
    for (const originalFile of originalFiles) {
      if (
        fileName.includes(originalFile.name.replace('.pdf', '')) ||
        fileName === originalFile.name
      ) {
        return originalFile.name;
      }
    }
    return fileName;
  };

  // Group files for display
  const groupedFiles = files.reduce<Record<string, File[]>>((groups, file) => {
    const originalFileName = getOriginalFileName(file.name);
    if (!groups[originalFileName]) {
      groups[originalFileName] = [];
    }
    groups[originalFileName].push(file);
    return groups;
  }, {});

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        p: 2,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      }}
    >
      <Typography variant="body2" sx={{ mb: 2 }}>
        Only PDF files are currently supported. Documents will be automatically split for better
        processing. Once upload starts, you can navigate away and it will continue in the
        background.
      </Typography>

      {fileTypeError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fileTypeError}
        </Alert>
      )}

      {processingMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {processingMessage}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button
            onClick={handleUploadClick}
            disabled={isUploading || isSplittingFiles}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Upload PDF Files
          </Button>

          {files.length > 0 && (
            <Button
              type="submit"
              variant="contained"
              disabled={!files.length || isUploading || isSplittingFiles}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Start Upload
            </Button>
          )}
        </Stack>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,application/pdf"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </form>

      {/* Display files, grouped by original file */}
      {Object.entries(groupedFiles).map(([originalFileName, fileGroup]) => {
        const info = splitInfo[originalFileName];
        const isSplit = info?.isSplit || false;

        return (
          <Card
            key={originalFileName}
            variant="outlined"
            sx={{
              mt: 2,
              backgroundColor: theme.palette.background.paper,
              borderColor: theme.palette.divider,
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.primary, fontWeight: 'medium' }}
                >
                  {originalFileName}
                </Typography>

                {isSplit && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      label={`Split into ${info.chunks} parts`}
                      color="primary"
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    <Tooltip title="Toggle details">
                      <IconButton
                        size="small"
                        onClick={() => toggleSplitDetails(originalFileName)}
                        sx={{ p: 0.5 }}
                      >
                        {showSplitDetails[originalFileName] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              {isSplit && (
                <Collapse in={showSplitDetails[originalFileName]}>
                  <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                    <Typography variant="body2">
                      This file has been split into {info.chunks} parts due to its size.
                      {info.totalPages > 0 &&
                        ` The original PDF contains ${info.totalPages} pages.`}
                      Each part will be processed individually for better performance.
                    </Typography>
                  </Alert>
                </Collapse>
              )}

              {fileGroup.map((file, index) => (
                <Box key={file.name} sx={{ mt: index > 0 ? 2 : 0, px: isSplit ? 1 : 0 }}>
                  {isSplit && fileGroup.length > 1 && (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}
                    >
                      Part {index + 1}: {file.name}
                    </Typography>
                  )}

                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: errors[file.name]
                        ? theme.palette.error.main
                        : theme.palette.text.secondary,
                    }}
                  >
                    {errors[file.name]
                      ? `Error: ${errors[file.name]}`
                      : progress[file.name] || 'Start Upload to Process'}
                  </Typography>

                  <LinearProgress
                    variant={
                      !progress[file.name]
                        ? 'determinate'
                        : progress[file.name]?.includes('complete') || errors[file.name]
                          ? 'determinate'
                          : 'indeterminate'
                    }
                    value={
                      !progress[file.name]
                        ? 0
                        : progress[file.name]?.includes('complete')
                          ? 100
                          : errors[file.name]
                            ? 0
                            : 0
                    }
                    sx={{
                      mt: 0.5,
                      backgroundColor: errors[file.name]
                        ? theme.palette.error.light
                        : theme.palette.primary.light,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: errors[file.name]
                          ? theme.palette.error.main
                          : theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        );
      })}

      {uploadComplete && (
        <Alert severity="success" sx={{ mt: 3 }}>
          Upload process started. You can navigate away from this page and the uploads will continue
          in the background.
        </Alert>
      )}
    </Box>
  );
};
